/**
 * One Projects ``tool_call`` block rendered as the live Evidence
 * sub-agent UI.
 *
 * Mounted from ``ProjectChatMessage`` when a tool_call's name is
 * either ``answer_with_evidence`` or ``lookup_evidence`` — both
 * dispatch to the same backend handler (``run_evidence_session``)
 * which streams Evidence SSE chunks back through the agent-event
 * bus as ``evidence.chunk`` envelopes. This component is the FE
 * read-side, with two sources (reconciliation contract):
 *
 *   1. **Persisted render payload** (authoritative once the call has
 *      completed) — ``result_provider_metadata.data.evidence`` on the
 *      merged tool item, rebuilt into a ``SessionConversation`` by
 *      ``buildEvidenceSessionFromPayload``. Survives reloads.
 *   2. **Live stream** (in-flight only) — the per-tool_call_id
 *      ``SessionConversation`` from the global atom.
 *
 * When both exist the persisted session wins: the BE accumulates the
 * answer directly from the streamer, so it is complete even when the
 * lossy live bus dropped frames mid-stream.
 *
 * Visual contract: no card chrome, no "Evidence sub-agent" header
 * pill, no status badge. The timeline IS the UI — visually
 * indistinguishable from asking the same question in the standalone
 * Evidence chat surface. That parity is the whole point of the work
 * (see ``docs/projects-evidence-subagent-fe.md``).
 *
 * Status mapping
 * --------------
 * The Projects message renderer derives a coarse ``status`` from the
 * harness (``running`` while no matching ``tool_result`` exists,
 * ``success`` / ``error`` / ``cancelled`` once it does). We use that to:
 *
 *   * ``running`` + no chunks yet → show a friendly "starting"
 *     loader inside ``EvidenceTimelineBody``.
 *   * ``running`` + chunks present → drive the live-pulse on the
 *     last timeline entry (the streaming caret on text, the spinning
 *     loader on the active search block).
 *   * ``success`` → terminal render; ``EvidenceTimelineBody``
 *     drops the caret/pulse.
 *   * ``error`` → render the timeline normally (the user can still
 *     read whatever the sub-agent produced before failing) plus a
 *     trailing error pill so the failure mode is visible.
 *   * ``cancelled`` → render any partial timeline normally and add a
 *     neutral cancellation note.
 */

import { TypographyCaption } from '@heidi/design-system/typography';
import { cn } from '@heidi/utils';
import { AlertTriangle } from 'lucide-react';
import { Suspense, useMemo } from 'react';

import { getWorkHostPort } from '../../work-home/work-home-ports';
import {
  CancelledNotice,
  StartingPlaceholder,
} from './evidence-subagent/EvidenceInlineStatus';

export type EvidenceSessionStatus = 'running' | 'success' | 'error' | 'cancelled';

export interface EvidenceSessionInlineBlockProps {
  /** Projects tool_call_id for the active ``answer_with_evidence`` /
   *  ``lookup_evidence`` call. ``undefined`` while a tool_call block
   *  is mid-creation; the component renders a minimal placeholder. */
  toolCallId: string | undefined;
  status: EvidenceSessionStatus;
  /** Question the parent agent passed in. Surfaced as a thin
   *  placeholder while the sub-agent is starting up so the user can
   *  see something useful before the first SSE chunk lands. */
  question?: string;
  /** Friendly error string from the tool_result, when ``status`` is
   *  ``error``. */
  errorMessage?: string;
  /** ``result_provider_metadata`` from the merged ``ToolUseItem``.
   *  Carries the persisted render payload at ``data.evidence`` once
   *  the call has completed; absent or payload-less for in-flight
   *  calls and pre-persistence results. */
  resultProviderMetadata?: Record<string, unknown>;
  onOpenSession?: (sessionId: string) => void;
  /** Slide Builder opener for pptx export entries — see ``EvidenceTimelineBody``. */
  onOpenPresentation?: (presentationId: string) => void;
  /** The searches are listed on the step's Working row — see ``EvidenceTimelineBody``. */
  searchesInWorkingBlock?: boolean;
  className?: string;
}

type EvidenceSessionLike = {
  timeline: readonly unknown[];
};

type EvidenceRenderPayloadLike = {
  truncated?: boolean;
  partial?: boolean;
  error?: unknown;
};

export const EvidenceSessionInlineBlock = ({
  toolCallId,
  status,
  question,
  errorMessage,
  resultProviderMetadata,
  onOpenSession,
  onOpenPresentation,
  searchesInWorkingBlock = false,
  className,
}: EvidenceSessionInlineBlockProps): JSX.Element | null => {
  const bridge = getWorkHostPort('bridge');
  const EvidenceTimelineBody = bridge.EvidenceTimelineBody;
  const liveSession = bridge.useProjectEvidenceSession(toolCallId) as
    | EvidenceSessionLike
    | undefined;
  // Memoised on the metadata reference: the merged item is rebuilt only
  // when the transport snapshot changes, and a stable session identity
  // keeps ``useTextGrouping`` / ``TextResponse`` from re-deriving on
  // unrelated renders.
  const persisted = useMemo(() => {
    if (!toolCallId) return undefined;
    const bridge = getWorkHostPort('bridge');
    const payload = bridge.readEvidenceRenderPayload(resultProviderMetadata) as
      | EvidenceRenderPayloadLike
      | undefined
      | null;
    if (!payload) return undefined;
    // The builder returns undefined when no chunk is renderable — fall
    // back to live/no-output behaviour rather than mounting an empty
    // timeline (which would show the "starting" loader on a terminal
    // call).
    const session = bridge.buildEvidenceSessionFromPayload(payload, toolCallId) as
      | EvidenceSessionLike
      | undefined
      | null;
    if (!session) return undefined;
    return { payload, session };
  }, [resultProviderMetadata, toolCallId]);

  const isStreaming = status === 'running' && !persisted;
  // Persisted wins once present; the live atom only renders in-flight
  // state. Drop a non-streaming live session with an empty timeline (the atom
  // isn't cleared on a boundary) so it routes to the status-aware placeholder
  // instead of spinning the empty-timeline loader. Kept while streaming or once any entry lands.
  const session =
    persisted?.session ??
    (isStreaming || liveSession?.timeline.length ? liveSession : undefined);

  if (!session) {
    // No chunks have landed yet for this tool_call_id. Could be:
    //   * The tool_call block just appeared in the polled snapshot
    //     and the first ``evidence.chunk`` envelope is in flight.
    //   * The agent-event bus subscription is paused (token churn,
    //     network blip) and we never got the chunks.
    //   * The tool returned synchronously without ever streaming
    //     (a bug; the handler always emits at least the synthesised
    //     text chunks).
    // A friendly "starting" placeholder is correct for all three.
    return (
      <StartingPlaceholder
        question={question}
        status={status}
        errorMessage={errorMessage}
        searchesInWorkingBlock={searchesInWorkingBlock}
        className={className}
      />
    );
  }

  return (
    <div className={cn('flex w-full flex-col gap-2', className)}>
      <Suspense fallback={null}>
        <EvidenceTimelineBody
          session={session}
          isStreaming={isStreaming}
          onOpenSession={onOpenSession}
          onOpenPresentation={onOpenPresentation}
          searchesInWorkingBlock={searchesInWorkingBlock}
        />
      </Suspense>
      {/* Degraded persisted payloads still render whatever is there
          — plus an honest marker, never a blank.
          ``partial`` on an errored call skips the extra note: the
          destructive pill below already explains the failure. */}
      {persisted?.payload.truncated === true ? (
        <TypographyCaption className="text-text-tertiary">
          Some Evidence results were truncated.
        </TypographyCaption>
      ) : null}
      {/* Surface the "incomplete results" note whenever the payload is
          partial OR carries an internal error, even if the tool itself
          reported success. A payload that errored mid-stream without the
          BE also setting ``partial`` would otherwise render degraded
          clinical evidence with no honesty marker. Skipped on
          ``status === 'error'`` — the destructive pill below already
          explains the failure. */}
      {(persisted?.payload.partial === true || persisted?.payload.error != null) &&
      status === 'success' ? (
        <TypographyCaption className="text-text-tertiary">
          Evidence search ended early — showing partial results.
        </TypographyCaption>
      ) : null}
      {status === 'error' && errorMessage && (
        <div
          className={cn(
            'flex items-start gap-2 rounded-md border border-validation-destructive-muted bg-validation-destructive-muted px-2.5 py-1.5 text-xs text-validation-destructive',
            'duration-300 animate-in fade-in'
          )}
        >
          <AlertTriangle className="mt-0.5 size-3.5 shrink-0" />
          <span className="break-words">{errorMessage}</span>
        </div>
      )}
      {status === 'cancelled' && <CancelledNotice />}
    </div>
  );
};
